import { useEffect, useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Pagination } from '../ui/pagination';
import {
  Download,
  Loader2,
  AlertCircle,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  ArrowLeft,
} from 'lucide-react';
import { drilldownAPI } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import {
  OTIF_TABS,
  OTIF_LINKABLE_FIELDS,
  formatDrilldownValue,
  createFieldLink,
  getOTIFStatusStyle,
  getOnTimeStatusStyle,
  downloadExportedData,
  type DrilldownField,
} from './drilldownUtils';

interface OTIFDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters?: Record<string, unknown>;
  contextLabel?: string;
  initialTab?: 'failed' | 'arrears' | 'positive' | 'total';
}

export default function OTIFDrilldownModal({
  isOpen,
  onClose,
  filters = {},
  contextLabel,
  initialTab,
}: OTIFDrilldownModalProps) {
  const { viewLevel, getViewQueryParams } = useView();
  const { formatDate } = useFormatters();

  const [activeTab, setActiveTab] = useState<string>(initialTab || 'failed');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<Array<Record<string, unknown>>>([]);
  const [fields, setFields] = useState<DrilldownField[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Second-level drill-down state
  const [showGRNDetails, setShowGRNDetails] = useState(false);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [selectedLineInfo, setSelectedLineInfo] = useState<string>('');
  const [grnRecords, setGrnRecords] = useState<Array<Record<string, unknown>>>([]);
  const [grnFields, setGrnFields] = useState<DrilldownField[]>([]);
  const [grnLoading, setGrnLoading] = useState(false);
  const [grnTotalCount, setGrnTotalCount] = useState(0);
  const [grnCurrentPage, setGrnCurrentPage] = useState(1);
  const [grnTotalPages, setGrnTotalPages] = useState(1);

  const fetchTabCounts = useCallback(async () => {
    const counts: Record<string, number> = {};
    const viewParams = getViewQueryParams();

    for (const tab of OTIF_TABS) {
      try {
        const response = await drilldownAPI.getDetails('otif_lines', {
          ...filters,
          ...tab.filter,
          view_level: viewLevel,
          ...viewParams,
          page: 1,
          page_size: 1,
        });
        counts[tab.key] = response.data.count;
      } catch {
        counts[tab.key] = 0;
      }
    }

    setTabCounts(counts);
  }, [filters, viewLevel, getViewQueryParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const activeTabConfig = OTIF_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.getDetails('otif_lines', {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
        page: currentPage,
        page_size: pageSize,
      });

      const { results, count } = response.data;
      setRecords(results.records);
      setFields(results.fields);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / pageSize));
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load OTIF data');
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab, currentPage, pageSize, viewLevel, getViewQueryParams]);

  const fetchGRNDetails = useCallback(async (lineId: number, lineInfo: string) => {
    setGrnLoading(true);
    setSelectedLineId(lineId);
    setSelectedLineInfo(lineInfo);
    setShowGRNDetails(true);
    setGrnCurrentPage(1);
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.getDetails('otif_grns', {
        ...filters,
        po_line_id: lineId,
        view_level: viewLevel,
        ...viewParams,
        page: 1,
        page_size: pageSize,
      });

      const { results, count } = response.data;
      setGrnRecords(results.records);
      setGrnFields(results.fields);
      setGrnTotalCount(count);
      setGrnTotalPages(Math.ceil(count / pageSize));
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load GRN details');
    } finally {
      setGrnLoading(false);
    }
  }, [filters, viewLevel, getViewQueryParams]);

  const fetchGRNPage = useCallback(async (page: number) => {
    if (!selectedLineId) return;

    setGrnLoading(true);
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.getDetails('otif_grns', {
        ...filters,
        po_line_id: selectedLineId,
        view_level: viewLevel,
        ...viewParams,
        page,
        page_size: pageSize,
      });

      const { results, count } = response.data;
      setGrnRecords(results.records);
      setGrnTotalCount(count);
      setGrnTotalPages(Math.ceil(count / pageSize));
      setGrnCurrentPage(page);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load GRN details');
    } finally {
      setGrnLoading(false);
    }
  }, [filters, selectedLineId, viewLevel, getViewQueryParams]);

  useEffect(() => {
    if (isOpen) {
      fetchTabCounts();
      fetchData();
    }
  }, [isOpen, fetchTabCounts, fetchData]);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setShowGRNDetails(false);
    }
  }, [activeTab, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab || 'failed');
    } else {
      setActiveTab(initialTab || 'failed');
      setCurrentPage(1);
      setShowGRNDetails(false);
      setSelectedLineId(null);
      setRecords([]);
      setGrnRecords([]);
    }
  }, [isOpen, initialTab]);

  const renderCellValue = (fieldKey: string, value: unknown, record: Record<string, unknown>, isGRNTable = false) => {
    const formattedValue = formatDrilldownValue(value, formatDate);
    const linkConfig = OTIF_LINKABLE_FIELDS[fieldKey];

    if (linkConfig && value) {
      const url = linkConfig.urlPattern(String(value), record);
      return createFieldLink(url, formattedValue);
    }

    // Special handling for OTIF status - make it clickable for GRN drill-down (only in main table)
    // CR-4662: Column is now bound to is_otif (boolean) instead of otif_status
    // (CharField) because otif_status can be stale for imported rows.
    if (fieldKey === 'is_otif' && record['id'] && !isGRNTable) {
      const { className, label } = getOTIFStatusStyle(value);
      return (
        <button
          onClick={() => fetchGRNDetails(record['id'] as number, `${record['po__po_number']} / ${record['line_number']}`)}
          className={`px-2 py-1 rounded text-xs font-medium ${className}`}
        >
          {label}
        </button>
      );
    }

    // Style on_time_status in GRN details table
    if (fieldKey === 'on_time_status') {
      const { className, label } = getOnTimeStatusStyle(value);
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${className}`}>
          {label}
        </span>
      );
    }

    return formattedValue;
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExporting(true);
    setShowExportMenu(false);

    const activeTabConfig = OTIF_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.exportData('otif_lines', format, {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
      });

      downloadExportedData(response.data, `otif_lines_${activeTab}_export`, format);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleBackFromGRN = useCallback(() => {
    setShowGRNDetails(false);
    setSelectedLineId(null);
    setGrnRecords([]);
  }, []);

  // CR-4543: Escape key navigates back from GRN details → Lines instead of closing modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showGRNDetails) {
        e.preventDefault();
        e.stopPropagation();
        handleBackFromGRN();
      }
    };
    if (isOpen && showGRNDetails) {
      window.addEventListener('keydown', handleKeyDown, true);
      return () => window.removeEventListener('keydown', handleKeyDown, true);
    }
  }, [isOpen, showGRNDetails, handleBackFromGRN]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              {showGRNDetails && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackFromGRN}
                  className="mb-2 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Lines
                </Button>
              )}
              <DialogTitle className="text-xl">
                {showGRNDetails ? `GRN Details - ${selectedLineInfo}` : 'OTIF Analysis'}
                {contextLabel && !showGRNDetails && (
                  <span className="text-muted-foreground font-normal ml-2">
                    - {contextLabel}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                {showGRNDetails
                  ? `${grnTotalCount.toLocaleString()} GRN records for this line`
                  : `Drill down into OTIF performance by line status`}
              </DialogDescription>
            </div>
            {!showGRNDetails && (
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={exporting || records.length === 0}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border z-50">
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('xlsx')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export as Excel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Tab Headers with Counts */}
        {!showGRNDetails && (
          <div className="border-b">
            <div className="flex">
              {OTIF_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <span className="text-lg font-bold">
                      {tabCounts[tab.key]?.toLocaleString() ?? '-'}
                    </span>
                    <span className="text-xs mt-0.5">{tab.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 min-h-0 overflow-auto">
          {(loading || grnLoading) ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading data...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>{error}</p>
              <Button variant="outline" size="sm" onClick={fetchData} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : showGRNDetails ? (
            // GRN Details Table
            <div className="overflow-x-auto">
              {grnRecords.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <p>No GRN records found for this line</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      {grnFields.map((field) => (
                        <th
                          key={field.field}
                          className="text-left py-3 px-4 text-sm font-medium whitespace-nowrap"
                        >
                          {field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grnRecords.map((record, idx) => (
                      <tr
                        key={String(record['id'] ?? idx)}
                        className="border-b hover:bg-muted/30 transition-colors"
                      >
                        {grnFields.map((field) => (
                          <td
                            key={field.field}
                            className="py-3 px-4 text-sm whitespace-nowrap"
                          >
                            {renderCellValue(field.field, record[field.field], record, true)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <p>No records found for this filter</p>
            </div>
          ) : (
            // PO Lines Table
            <div>
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    {fields.map((field) => (
                      <th
                        key={field.field}
                        className="text-left py-3 px-4 text-sm font-medium whitespace-nowrap"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr
                      key={String(record['id'] ?? idx)}
                      className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => fetchGRNDetails(Number(record['id']), `${record['po__po_number']} / Line ${record['line_number']}`)}
                    >
                      {fields.map((field) => (
                        <td
                          key={field.field}
                          className="py-3 px-4 text-sm whitespace-nowrap"
                        >
                          {renderCellValue(field.field, record[field.field], record)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && !error && (
          showGRNDetails ? (
            grnTotalPages > 1 && (
              <div className="flex-shrink-0 -mt-4">
                <Pagination
                  currentPage={grnCurrentPage}
                  totalPages={grnTotalPages}
                  totalCount={grnTotalCount}
                  itemsPerPage={pageSize}
                  onPageChange={fetchGRNPage}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                  itemLabel="GRN records"
                />
              </div>
            )
          ) : (
            totalPages > 1 && (
              <div className="flex-shrink-0 -mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                  itemLabel="lines"
                />
              </div>
            )
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
